import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginUser } from "../redux/authSlice";
import { useTranslation } from "react-i18next";
import "./Login_copy.css";
import i18n from "../i18n";

/* fires once when element enters viewport */
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [focused, setFocused] = useState("");
  const [mounted, setMounted] = useState(false);
  const [cardRef, cardVisible] = useInView(0.1);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {t} = useTranslation();
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.email || !form.password)
      return setError(t("email_password_required"));
    setLoading(true);
    try {
      await dispatch(loginUser({ email: form.email, password: form.password }))
        .unwrap()
        .then((res)=>{
          let user = res?.user;
          if(user && user?.role === "admin"){
            navigate("/create-profile");
          } else {
            navigate("/dashboard");
          }
        })
        .catch((err) =>
          setError(err?.error || t("login_failed")),
        );
    } catch (err) {
      setError(err.response?.data?.error || t("login_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  /* stable particle data */
  const particles = useRef(
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: 5 + ((i * 4.7) % 90),
      y: 5 + ((i * 7.3) % 35),
      size: 2 + (i % 4),
      dur: 7 + (i % 6) * 1.5,
      delay: (i * 0.6) % 9,
    })),
  ).current;

  return (
    <>
 

      <div className="lp" data-lang={i18n.language}>
        {/* ══ HERO ══ */}
        <div className="lp-hero">
          <div className="lp-hero-bg" />
          <div className="lp-hero-ov" />

          {/* particles */}
          <div className="lp-ptcl">
            {particles.map((p) => (
              <span
                key={p.id}
                className="lp-dot"
                style={{
                  left: `${p.x}%`,
                  bottom: `${p.y}%`,
                  width: p.size,
                  height: p.size,
                  "--d": `${p.dur}s`,
                  "--dl": `${p.delay}s`,
                }}
              />
            ))}
          </div>

          {/* SVG curve */}
          <div className="lp-curve">
            <svg
              viewBox="0 0 72 900"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M0,0 Q72,450 0,900 L72,900 L72,0 Z" fill="#FFF8F0" />
            </svg>
          </div>

          <div className="lp-hbody">
            {/* <div className="lp-badge">
            </div> */}

            <h1 className="lp-h1">
              {t("step_back_title")}
            </h1>

            <p className="lp-sub">
              {t("step_back_subtitle")}
            </p>

            <div className="lp-pills">
              {[
                { t: t("verified_profiles"), s: t("genuine_trusted") },
                { t: t("100_secure"),        s: t("privacy_first")   },
                { t: t("marriages_count"),   s: t("success_stories") },
              ].map((p) => (
                <div className="lp-pill" key={p.t}>
                  <div>
                    <div className="lp-pill-t">{p.t}</div>
                    <div className="lp-pill-s">{p.s}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* <div className="lp-stats">
              {[
                ["10K+", "Profiles"],
                ["2K+", "Matches"],
                ["15+", "States"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="lp-sn">{n}</div>
                  <div className="lp-sl">{l}</div>
                </div>
              ))}
            </div> */}
          </div>
        </div>

        {/* ══ PANEL ══ */}
        <div className="lp-panel">
          <div
            ref={cardRef}
            className={`lp-card${cardVisible || mounted ? " show" : ""}`}
          >
            {/* Tabs */}
            <div className="lp-tabs">
              <button
                className={`lp-tab${activeTab === "login" ? " on" : ""}`}
                onClick={() => setActiveTab("login")}
              >
                {t("login")}
              </button>
              <button
                className={`lp-tab${activeTab === "signup" ? " on" : ""}`}
                onClick={() => {
                  setActiveTab("signup");
                  navigate("/register");
                }}
              >
                {t("sign_up")}
              </button>
            </div>

            {/* Head */}
            <div className="lp-ch">
              <h2>{t("welcome_back")}</h2>
              <p>{t("continue_journey")}</p>
            </div>

            {/* Error */}
            {error && (
              <div className="lp-err">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <circle
                    cx="12"
                    cy="16"
                    r=".6"
                    fill="currentColor"
                    stroke="none"
                  />
                </svg>
                {error}
              </div>
            )}

            {/* Fields */}
            <div className="lp-flds">
              {/* Email */}
              <div className={`lp-field${focused === "email" ? " foc" : ""}`}>
                <label>{t("email_or_mobile")}</label>
                <div
                  className={`lp-box${focused === "email" ? " foc" : ""}${error && !form.email ? " err" : ""}`}
                >
                  <span className="lp-ico">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setFocused("email")}
                    onBlur={() => setFocused("")}
                    placeholder="user@niran.com"
                    autoComplete="off"
                  />
                </div>
              </div>

              {/* Password */}
              <div
                className={`lp-field${focused === "password" ? " foc" : ""}`}
              >
                <label>{t("password")}</label>
                <div
                  className={`lp-box${focused === "password" ? " foc" : ""}${error && !form.password ? " err" : ""}`}
                >
                  <span className="lp-ico">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <input
                    name="password"
                    type={showPass ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setFocused("password")}
                    onBlur={() => setFocused("")}
                    placeholder="••••••••"
                  />
                  <button
                    className="lp-eye"
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                  >
                    {showPass ? (
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="lp-frow">
                  <button className="lp-forgot" type="button" onClick={() => navigate("/ForgotPassword")}>
                    {t("forgot_password")}
                  </button>
                </div>
              </div>
            </div>

            {/* Primary CTA */}
            <button
              className="lp-btn"
              onClick={handleSubmit}
              disabled={loading}
              onMouseDown={(e) => {
                const rpl = e.currentTarget.querySelector(".lp-btn-rpl");
                if (!rpl) return;
                const s = document.createElement("span");
                const r = e.currentTarget.getBoundingClientRect();
                const size = Math.max(r.width, r.height);
                s.style.cssText = `width:${size}px;height:${size}px;left:${e.clientX - r.left - size / 2}px;top:${e.clientY - r.top - size / 2}px`;
                rpl.appendChild(s);
                setTimeout(() => s.remove(), 600);
              }}
            >
              <div className="lp-btn-rpl" />
              {loading ? <span className="lp-spin" /> : t("login")}
            </button>

            {/* <div className="lp-or">
              <span>OR</span>
            </div> */}

            {/* Alt buttons */}
            <div className="lp-alts">
              {/* <button className="lp-alt" type="button">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="5" y="2" width="14" height="20" rx="2" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                </svg>
                Login with OTP
              </button>
              <button className="lp-alt" type="button">
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </button> */}
            </div>

            {/* Footer */}
            <div className="lp-foot">
              {t("no_profile_create")}{" "}
              <button onClick={() => navigate("/register")}>
                {t("no_profile_create_btn")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
